import { useMemo, useCallback } from 'react';
import { Text } from '@mercator-gcp/grout';
import AIMarker from 'components/elements/AIMarker';
import { EmptyField } from 'components/elements/EmptyField';
import { formatInteger } from 'utils/format';
import { isNumber } from 'utils/validate';
import type { ProjectSize as TProjectSize } from 'types';

type TProps = {
  reportedMinSize?: TProjectSize['reported_sqft_min'];
  reportedMaxSize?: TProjectSize['reported_sqft_max'];
  estimatedMinSize?: TProjectSize['estimated_sqft_min'];
  estimatedMaxSize?: TProjectSize['estimated_sqft_max'];
};

const ProjectSize = ({
  reportedMinSize,
  reportedMaxSize,
  estimatedMinSize,
  estimatedMaxSize,
}: TProps) => {
  const generateLabel = useCallback((min: number, max: number) => {
    if (min === max) {
      return `${formatInteger(max)} sqft`;
    }

    return `${formatInteger(min)} - ${formatInteger(max)} sqft`;
  }, []);

  const label = useMemo(() => {
    if (isNumber(reportedMinSize) && isNumber(reportedMaxSize) && reportedMaxSize !== 0) {
      return generateLabel(reportedMinSize || 0, reportedMaxSize || 0);
    }
    if (
      isNumber(estimatedMinSize) &&
      isNumber(estimatedMaxSize) &&
      estimatedMaxSize !== 0
    ) {
      return generateLabel(estimatedMinSize || 0, estimatedMaxSize || 0);
    }

    return <EmptyField />;
  }, [reportedMinSize, reportedMaxSize, estimatedMinSize, estimatedMaxSize]);

  const isGeneratedByAI = useMemo(() => {
    if (reportedMinSize && reportedMaxSize) {
      return false;
    }
    if (estimatedMinSize && estimatedMaxSize) {
      return true;
    }

    return false;
  }, [reportedMinSize, reportedMaxSize, estimatedMinSize, estimatedMaxSize]);

  if (isGeneratedByAI) {
    return (
      <AIMarker>
        <Text>{label}</Text>
      </AIMarker>
    );
  }

  return <Text>{label}</Text>;
};

export default ProjectSize;
